import {
  Box,
  Checkbox,
  DestructiveModal,
  ErrorModal,
  Flex,
  ResponsiveTable,
  Text,
  TextButton,
  Toast,
} from '@modusign/design-system';
import { ReactNode, useState } from 'react';

import CenterLoadingSpinner from '@app/components/CenterLoadingSpinner';
import GlobalLoadingSpinner from '@app/components/Loading/GlobalLoadingSpinner';
import Overlay from '@app/components/Overlay';
import SelectLimit from '@app/components/SelectLimit';
import SwitchablePagination from '@app/components/SwitchablePagination';
import ContactManagementModal from '@app/features/addressBook/components/ContactManagementModal';
import ContactsUtilityBar from '@app/features/addressBook/components/ContactsUtilityBar';
import SearchBar from '@app/features/addressBook/components/SearchBar';
import { ADDRESS_BOOK_ERROR_MESSAGE } from '@app/features/addressBook/constants';
import useContacts from '@app/features/addressBook/hooks/useContacts';
import useDeleteContacts from '@app/features/addressBook/hooks/useDeleteContacts';
import useDeleteContactsById from '@app/features/addressBook/hooks/useDeleteContactsById';
import useSearchFilterContext from '@app/features/addressBook/hooks/useSearchFilterContext';
import useUpdateContactsById from '@app/features/addressBook/hooks/useUpdateContactsById';

import ContactListItem from '../ContactListItem';

export const CONTACT_LIST_NAME_COLUMN_WIDTH = 200;
export const CONTACT_LIST_CONTACT_COLUMN_WIDTH = 344;
export const CONTACT_LIST_CREATED_AT_WIDTH = 100;

function ContactList() {
  const [contactId, setContactId] = useState<string | null>(null);
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const { limit, maxPage, page, setLimit, searchKeyword, setMaxPage, setPage } =
    useSearchFilterContext();

  const { data, error, isLoading, refetch } = useContacts(
    {
      limit,
      offset: (page - 1) * limit,
      name: searchKeyword || undefined,
      companyName: searchKeyword || undefined,
    },
    {
      onSuccess: (data) => {
        setMaxPage((data.count && Math.ceil(data.count / limit)) ?? 1);
      },
    }
  );

  const {
    error: deleteContactsError,
    isLoading: isDeleteContactsLoading,
    mutate: deleteContacts,
    reset: resetDeleteContacts,
  } = useDeleteContacts({
    onSuccess: () => {
      Toast.success({ description: '연락처를 삭제했습니다.' });
      setCheckedIds([]);
      refetch();
    },
  });

  const isCheckedAll = checkedIds.length === limit;
  const indeterminate = checkedIds.length > 0 && checkedIds.length < limit;
  const isEmpty = !data || data.count === 0;

  const {
    error: updateContactError,
    isLoading: isUpdateContactLoading,
    mutate: updateContact,
    reset: resetUpdateContact,
  } = useUpdateContactsById({
    onSuccess: () => {
      Toast.success({ description: '변경사항을 적용했습니다.' });
      refetch();
    },
  });

  const {
    error: deleteContactByIdError,
    isLoading: isDeleteContactByIdLoading,
    mutate: deleteContactById,
    reset: resetDeleteContactById,
  } = useDeleteContactsById({
    onSuccess: () => {
      Toast.success({ description: '연락처를 삭제했습니다.' });
      refetch();
    },
  });

  return (
    <>
      {checkedIds.length > 0 ? (
        <ContactsUtilityBar
          contactIds={checkedIds}
          onDelete={() => setIsDeleteConfirmOpen(true)}
          onReset={() => setCheckedIds([])}
        />
      ) : (
        <SearchBar
          resultCount={data?.count ?? 0}
          title="연락처"
          onReload={() => {
            refetch();
          }}
        />
      )}
      <Box>
        <ResponsiveTable.Table display={['block', null, 'table-row-group']}>
          <ResponsiveTable.THead>
            <ResponsiveTable.THeadRow>
              <ResponsiveTable.TH
                display={['none', null, 'table-cell']}
                sx={{ width: '40px', verticalAlign: 'middle' }}
              >
                <Checkbox
                  checked={isCheckedAll}
                  data-testid="contact-list-all-checkbox"
                  indeterminate={indeterminate}
                  onChange={() => {
                    if (indeterminate || isCheckedAll) {
                      setCheckedIds([]);
                    } else if (data) {
                      setCheckedIds(data.contacts.map((contact) => contact.id));
                    }
                  }}
                />
              </ResponsiveTable.TH>
              <ResponsiveTable.TH sx={{ width: `${CONTACT_LIST_NAME_COLUMN_WIDTH}px` }}>
                <Text as="p" color="textNeutralVeryLight" fontSize="sm">
                  이름
                </Text>
              </ResponsiveTable.TH>
              <ResponsiveTable.TH sx={{ width: `${CONTACT_LIST_CONTACT_COLUMN_WIDTH}px` }}>
                <Text as="p" color="textNeutralVeryLight" fontSize="sm">
                  전송 수단
                </Text>
              </ResponsiveTable.TH>
              <ResponsiveTable.TH
                display={['none', null, 'table-cell']}
                sx={{ width: `${CONTACT_LIST_CREATED_AT_WIDTH}px` }}
              >
                <Text as="p" color="textNeutralVeryLight" fontSize="sm">
                  생성일
                </Text>
              </ResponsiveTable.TH>
            </ResponsiveTable.THeadRow>
          </ResponsiveTable.THead>
          <ResponsiveTable.TBody>
            {data &&
              data.contacts.length > 0 &&
              data.contacts.map((item) => {
                const thisContactChecked = checkedIds.includes(item.id);
                return (
                  <ContactListItem
                    checked={thisContactChecked}
                    key={`${item.id}-${thisContactChecked}`}
                    {...item}
                    onCheckClick={() => {
                      if (thisContactChecked) {
                        setCheckedIds((prev) => prev.filter((id) => id !== item.id));
                      } else {
                        setCheckedIds((prev) => [...prev, item.id]);
                      }
                    }}
                    onClick={() => setContactId(item.id)}
                  />
                );
              })}
            <ContactListMessages
              error={error}
              isEmpty={isEmpty}
              isLoading={isLoading}
              searchKeyword={searchKeyword}
              onRetry={refetch}
            />
          </ResponsiveTable.TBody>
        </ResponsiveTable.Table>
        {contactId && (
          <ContactManagementModal
            contactId={contactId}
            isOpen
            onClose={() => setContactId(null)}
            onCopyContactSucceeded={() => {
              setContactId(null);
              if (page === 1) {
                refetch();
              } else {
                setPage(1);
              }
            }}
            onDeleteSubmit={deleteContactById}
            onUpdateSubmit={(formValues) => {
              updateContact({
                id: contactId,
                ...formValues,
              });
            }}
          />
        )}
        <DestructiveModal
          confirmButtonLabel="삭제"
          isOpen={isDeleteConfirmOpen}
          title="연락처 삭제"
          width="xs"
          onClose={() => setIsDeleteConfirmOpen(false)}
          onConfirm={() => {
            setIsDeleteConfirmOpen(false);
            deleteContacts(checkedIds);
          }}
        >
          <Text as="p" textAlign="center">
            {checkedIds.length}개의 연락처를 삭제할까요?
            <br />
            삭제된 연락처는 복구할 수 없습니다.
          </Text>
        </DestructiveModal>
        <ErrorModal
          error={updateContactError}
          errorMessageMapper={ADDRESS_BOOK_ERROR_MESSAGE}
          isOpen
          title="연락처 수정 실패"
          onClose={() => {
            resetUpdateContact();
          }}
        />
        <ErrorModal
          error={deleteContactByIdError}
          errorMessageMapper={ADDRESS_BOOK_ERROR_MESSAGE}
          isOpen
          title="연락처 삭제 실패"
          onClose={() => {
            resetDeleteContactById();
          }}
        />
        <ErrorModal
          error={deleteContactsError}
          errorMessageMapper={ADDRESS_BOOK_ERROR_MESSAGE}
          isOpen
          title="연락처 삭제 실패"
          onClose={() => {
            resetDeleteContacts();
          }}
        />
        {(isUpdateContactLoading || isDeleteContactByIdLoading || isDeleteContactsLoading) && (
          <Overlay>
            <GlobalLoadingSpinner />
          </Overlay>
        )}
      </Box>
      {!isEmpty && (
        <Box mt={9} position="relative" pt={[9, null, 0]} textAlign="center">
          <SwitchablePagination
            currentPage={page}
            maxPage={maxPage}
            onClick={(nextPage: number) => setPage(nextPage)}
          />
          <Box
            position="absolute"
            right={0}
            sx={{
              transform: 'translateY(-50%)',
            }}
            top={[0, null, '50%']}
            width="120px"
          >
            <SelectLimit
              limit={limit}
              onChange={(limitValue: number) => {
                setCheckedIds([]);
                setLimit(limitValue);
              }}
            />
          </Box>
        </Box>
      )}
    </>
  );
}

export default ContactList;

function MessageContainer({ children }: { children: ReactNode }) {
  return (
    <ResponsiveTable.TBodyRow>
      <ResponsiveTable.TD colSpan={4} textAlign="center">
        <Flex alignItems="center" flexDirection="column" justifyContent="center" minHeight="200px">
          {children}
        </Flex>
      </ResponsiveTable.TD>
    </ResponsiveTable.TBodyRow>
  );
}

function ContactListMessages({
  error,
  isEmpty,
  isLoading,
  searchKeyword,
  onRetry,
}: {
  error: Error | null;
  isEmpty: boolean;
  isLoading: boolean;
  searchKeyword: string;
  onRetry: () => void;
}) {
  if (isLoading) {
    return (
      <MessageContainer>
        <CenterLoadingSpinner color="gray" size={50} testId="loading-spinner" />
      </MessageContainer>
    );
  }

  if (error) {
    return (
      <MessageContainer>
        <Box>
          {searchKeyword
            ? `'${searchKeyword}' 검색 결과를 불러오지 못했습니다.`
            : '연락처를 불러오지 못했습니다.'}
        </Box>
        <Box>
          <TextButton onClick={onRetry}>다시 시도</TextButton>
        </Box>
      </MessageContainer>
    );
  }

  if (isEmpty) {
    return (
      <MessageContainer>
        {searchKeyword ? (
          <>{`'${searchKeyword}'`} 검색 결과가 없습니다.</>
        ) : (
          <>
            연락처가 없습니다.
            <br />
            연락처를 추가하여 서명 요청에 주소록을 이용해 보세요.
          </>
        )}
      </MessageContainer>
    );
  }

  return null;
}
